import { useEffect } from 'react';

interface BlogSEOProps {
  pageTitle: string;
  pageDescription: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: string;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  isArticle?: boolean;
  articleSchema?: Record<string, unknown>;
}

export default function BlogSEO({
  pageTitle,
  pageDescription,
  canonicalPath,
  ogImage = 'https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/283c7fb27389007554f2f7045b266b94.png',
  ogType = 'website',
  publishedTime,
  modifiedTime,
  tags,
  isArticle = false,
  articleSchema,
}: BlogSEOProps) {
  useEffect(() => {
    document.title = `${pageTitle} | ClearMind Club`;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageDescription);
    setMeta('keywords', tags?.join(', ') || 'cognitive wellness, mind training, focus, nootropics, productivity');
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:url', `https://clearmind.club${canonicalPath}`, true);
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDescription);
    setMeta('twitter:image', ogImage);

    if (isArticle) {
      setMeta('og:type', 'article', true);
      if (publishedTime) setMeta('article:published_time', publishedTime, true);
      if (modifiedTime) setMeta('article:modified_time', modifiedTime, true);
      if (tags?.length) {
        tags.forEach((tag) => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        });
      }
    }

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `https://clearmind.club${canonicalPath}`);

    let schemaScript: HTMLScriptElement | null = null;
    if (articleSchema) {
      const existingSchema = document.querySelector('script[data-blog-schema]');
      if (existingSchema) existingSchema.remove();
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-blog-schema', 'true');
      schemaScript.textContent = JSON.stringify(articleSchema, null, 2);
      document.head.appendChild(schemaScript);
    }

    return () => {
      if (schemaScript) schemaScript.remove();
      const articleTags = document.querySelectorAll('meta[property^="article:"]');
      articleTags.forEach((t) => t.remove());
    };
  }, [pageTitle, pageDescription, canonicalPath, ogImage, ogType, publishedTime, modifiedTime, tags, isArticle, articleSchema]);

  return null;
}